<template>
  <section
    class="dropdown-form-shadow flex max-h-80 w-full flex-1 flex-col overflow-hidden rounded-xl bg-white"
  >
    <TownFromShimmer v-if="townsFrom.query.isFetching.value" class="flex-1" />

    <UiErrorCard
      v-else-if="townsFrom.query.isError.value"
      class="m-2 px-2 py-4 text-center"
      @onReload="townsFrom.query.refetch"
    />

    <ul v-else-if="!isEmpty(townsByQuery)" class="flex-1 overflow-auto">
      <li
        v-for="town in townsByQuery"
        :id="`town-${town.id}`"
        :key="town.id"
        class="p-1"
      >
        <button
          type="button"
          class="relative flex w-full items-center rounded-xl px-3 py-2 text-left text-gray-500 outline-none duration-200 hover:bg-gray-150 hover:text-black focus:bg-gray-75 focus:outline-none"
          :class="{
            'text-black': selectedTown?.id === town.id,
          }"
          @click="onSelect(town)"
        >
          <span
            v-if="selectedTown?.id === town.id"
            class="absolute left-0 h-8 w-[3px] rounded-xl bg-black"
          ></span>
          {{ town.name }}
        </button>
      </li>
    </ul>

    <div v-else class="m-1 rounded-xl bg-gray-75 p-3 text-center text-sm">
      <h1 class="font-semibold">
        {{ $t('search-form.departure.desktop.empty-search.title') }}
      </h1>

      <p class="mt-3 leading-relaxed">
        {{ $t('search-form.departure.desktop.empty-search.description') }}
      </p>
    </div>
  </section>
</template>

<script lang="ts" setup>
  import { computed, onMounted } from 'vue';
  import { isEmpty } from 'lodash-es';
  import type { Tourflow } from '@aviata/platform-types';

  import { UiErrorCard } from '@shared/ui';
  import { useTownFromQuery } from '@entities/town-from';
  import TownFromShimmer from './TownFromShimmer.vue';

  const props = defineProps<{
    query: string;
    selectedTown?: Tourflow.TownFrom;
    isFilterByQueryEnabled: boolean;
  }>();

  const emit = defineEmits<{
    onSelect: [town: Tourflow.TownFrom];
  }>();

  const townsFrom = useTownFromQuery();

  const townsByQuery = computed<Tourflow.TownFrom[]>(() => {
    if (!props.isFilterByQueryEnabled) {
      return townsFrom.query.data.value || [];
    }

    return (
      townsFrom.query.data.value?.filter(town =>
        town.name.toLowerCase().includes(props.query.toLowerCase()),
      ) || []
    );
  });

  function onSelect(town: Tourflow.TownFrom) {
    emit('onSelect', town);
  }

  onMounted(() => {
    townsFrom.init();

    if (!props.selectedTown) {
      return;
    }

    const selectedTownElement = document.querySelector(
      `#town-${props.selectedTown.id}`,
    );
    selectedTownElement?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  });
</script>
